import * as React from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Col, Container, Row } from 'reactstrap';
import Select from 'react-select';
import { config } from 'react-spring';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Lottie } from '@alfonmga/react-lottie-light-ts';
import newtheme from '../../newtheme';
import NoaCarousel from '../carousel/NoaCarousel';
import IconCircledLeftArrow from '../../assets/icons/IconCircledLeftArrow';
import IconCircledRightArrow from '../../assets/icons/IconCircledRightArrow';
import ISection from '../../types/models/ISection';
import { getSectionsV4, getSingleSectionV4 } from '../../actions/section';
import { IState } from '../../reducers';
import ISectionWithPlaylists from '../../types/models/ISectionWithPlaylists';
import playButtonAnim from '../../assets/lottie/playButtonAnim.json';
import pauseButtonAnim from '../../assets/lottie/pauseButtonAnim.json';
import { mod } from '../../utils/number';
import { mpTrackButtonClick } from '../../tracking/mixpanel';
import { MpTrackingButtons } from '../../config/buttons';
import IPlaylistNew from '../../types/models/IPlaylistNew';
import ISubscriptionProduct from '../../domaindriven/subscribe/types/ISubscriptionProduct';
import { getSelectedSubscriptionProduct } from '../../domaindriven/subscribe/state/selectors';
import { ILandingPageSeriesCarousel } from '../../domaindriven/landing/types/ILandingPageSeriesCarousel';

interface ILocalProps {
    sectionData: ILandingPageSeriesCarousel;
}

interface ILocalState {
    selectedCategoryOption: any;
    currentSlide: number;
    isPlaying: boolean;
}

interface IPropsFromState {
    loading: boolean;
    sections: ISection[];
    selectedSection: ISectionWithPlaylists;
    subscriptionProduct?: ISubscriptionProduct;
}

interface IPropsFromDispatch {
    getSectionsV4: typeof getSectionsV4;
    getSingleSectionV4: typeof getSingleSectionV4;
}

class SeriesExplainer extends React.Component<IPropsFromDispatch & IPropsFromState & ILocalProps, ILocalState> {
    public audio: HTMLAudioElement | null = null;

    constructor(props) {
        super(props);

        this.state = {
            selectedCategoryOption: null,
            currentSlide: 0,
            isPlaying: false,
        };
    }

    public componentDidMount() {
        this.props.getSectionsV4();
    }

    public componentDidUpdate() {
        if (!this.state.selectedCategoryOption && this.props.sections && this.props.sections.length > 0) {
            const selectedOption = this.getCategoryOptions()[0];
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ selectedCategoryOption: selectedOption });
            this.props.getSingleSectionV4(selectedOption.value.url);
        }
    }

    public handleCategorySelected = (selectedCategoryOption: any) => {
        this.setState({ selectedCategoryOption });
        this.props.getSingleSectionV4(selectedCategoryOption.value.url);
        mpTrackButtonClick(selectedCategoryOption.value.trackingButton);
    };

    public getCategoryOptions = () => {
        if (!this.props.sections) {
            return [];
        }

        const options: any = [];

        const business = this.extractSectionFromList(this.props.sections, 'Markets & Economics');
        // const politics = this.extractSectionFromList(this.props.sections, "Trump Politics");
        const tech = this.extractSectionFromList(this.props.sections, 'Science & Technology');
        const workCareers = this.extractSectionFromList(this.props.sections, 'Work & Careers');
        const worldAffairs = this.extractSectionFromList(this.props.sections, 'World Affairs');
        if (this.props.sectionData.defaultCategoryId) {
            const defaultCategory = this.props.sections.find(
                (section) => section.id === this.props.sectionData.defaultCategoryId,
            );
            if (defaultCategory) {
                this.addDropdownOption(
                    options,
                    defaultCategory.name,
                    MpTrackingButtons.SELECT_SERIES_CATEGORY_DEFAULT,
                    defaultCategory,
                );
            }
        }

        this.addDropdownOption(options, 'Business', MpTrackingButtons.SELECT_SERIES_CATEGORY_BUSINESS, business);
        this.addDropdownOption(
            options,
            'Science & Technology',
            MpTrackingButtons.SELECT_SERIES_CATEGORY_SCIENCE_TECH,
            tech,
        );
        this.addDropdownOption(
            options,
            'Work & Careers',
            MpTrackingButtons.SELECT_SERIES_CATEGORY_WORK_CAREERS,
            workCareers,
        );
        this.addDropdownOption(
            options,
            'World Affairs',
            MpTrackingButtons.SELECT_SERIES_CATEGORY_WORLD_AFFAIRS,
            worldAffairs,
        );

        return options;
    };

    public extractSectionFromList = (sections: ISection[], sectionName: string) =>
        this.props.sections.find((s) => s.name === sectionName);

    public addDropdownOption = (
        dropdownOptions: any[],
        name: string,
        trackingButton: MpTrackingButtons,
        section?: ISection,
    ) => {
        if (section) {
            dropdownOptions.push({
                label: name,
                value: {
                    trackingButton,
                    url: `${section.url}?limit=20`,
                },
            });
        }
    };

    public getSelectedPlaylists = () => {
        if (!this.props.selectedSection || !this.props.selectedSection.playlists) {
            return [];
        }

        const playlistsWithoutLongForm = this.props.selectedSection.playlists.filter((p) => p.numArticles > 2);
        return playlistsWithoutLongForm.slice(0, 5);
    };

    public getSeriesSlides = () => {
        if (this.props.selectedSection) {
            const topPlaylists = this.getSelectedPlaylists();

            const seriesCards = topPlaylists?.map((p, index) => ({
                key: p.id,
                content: (
                    <div
                        className={css(styles.seriesCard)}
                        style={{
                            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.56) 100%), url("${p.image}")`,
                            // background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("${p.image}")`,
                        }}>
                        <div className={css(styles.titleAndButton)}>
                            <h1 className={css(styles.title)}>{p.name}</h1>
                            {index === this.state.currentSlide && (
                                <div className={css(styles.button)} onClick={() => this.playOrPauseAudio(p)}>
                                    <Lottie
                                        config={{
                                            loop: !this.state.isPlaying,
                                            autoplay: true,
                                            animationData: this.state.isPlaying ? pauseButtonAnim : playButtonAnim,
                                            rendererSettings: {
                                                preserveAspectRatio: 'xMidYMid slice',
                                            },
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ),
            }));

            seriesCards?.map((slide, index) => ({ ...slide, onClick: (e: Event) => this.onSlideClick(e, index) }));

            return seriesCards as any;
        }

        const placeholderCards = [
            {
                key: 1,
                content: <div className={css(styles.seriesCard)} />,
            },
            {
                key: 2,
                content: <div className={css(styles.seriesCard)} />,
            },
            {
                key: 3,
                content: <div className={css(styles.seriesCard)} />,
            },
            {
                key: 4,
                content: <div className={css(styles.seriesCard)} />,
            },
            {
                key: 5,
                content: <div className={css(styles.seriesCard)} />,
            },
        ].map((slide, index) => ({ ...slide, onClick: (e: Event) => this.onSlideClick(e, index) }));

        return placeholderCards;
    };

    public onSlideClick = (e: Event, index: number) => {
        e.stopPropagation();

        this.changeSlideTo(index);
    };

    public goToLeftSlide = () => {
        const selectedPlaylists = this.getSelectedPlaylists();

        if (!selectedPlaylists) {
            return;
        }

        mpTrackButtonClick(MpTrackingButtons.SHOW_NEXT_SERIES);

        const numPlaylistsInSection = selectedPlaylists.length;
        const currentSlide = mod(this.state.currentSlide - 1, numPlaylistsInSection);
        this.changeSlideTo(currentSlide);
    };

    public goToRightSlide = () => {
        const selectedPlaylists = this.getSelectedPlaylists();

        if (!selectedPlaylists) {
            return;
        }

        mpTrackButtonClick(MpTrackingButtons.SHOW_NEXT_SERIES);

        const numPlaylistsInSection = selectedPlaylists.length;
        const currentSlide = mod(this.state.currentSlide + 1, numPlaylistsInSection);
        this.changeSlideTo(currentSlide);
    };

    public changeSlideTo = (index: number) => {
        this.setState({ currentSlide: index });
        this.pauseAudio();
    };

    public initAudio = () => {
        if (this.audio) {
            return;
        }

        this.audio = new Audio();

        this.audio.onplay = () => {
            this.setState({ isPlaying: true });
        };

        this.audio.onpause = () => {
            this.setState({ isPlaying: false });
        };

        this.audio.onended = () => {
            this.pauseAudio();
        };
    };

    public playOrPauseAudio = (playlist: IPlaylistNew) => {
        if (!this.audio) {
            this.initAudio();
        }

        if (!this.audio) {
            return;
        }

        if (this.audio.src === playlist.audioPreview) {
            if (this.audio.paused) {
                this.playAudio();
                mpTrackButtonClick(MpTrackingButtons.PLAY_PREVIEW, undefined, playlist.id, playlist.name);
            } else {
                this.pauseAudio();
                mpTrackButtonClick(MpTrackingButtons.PAUSE_PREVIEW, undefined, playlist.id, playlist.name);
            }
            return;
        }

        this.audio.src = playlist.audioPreview;
        this.audio.load();
        this.playAudio();
        mpTrackButtonClick(MpTrackingButtons.PLAY_PREVIEW, undefined, playlist.id, playlist.name);
    };

    public playAudio = () => {
        if (this.audio) {
            this.audio.play();
            this.setState({ isPlaying: true });
        }
    };

    public pauseAudio = () => {
        if (this.audio) {
            this.audio.pause();
            this.setState({ isPlaying: false });
        }
    };

    public renderSeriesCarouselHeading = () => {
        const { subscriptionProduct } = this.props;

        return (
            <h3 className={css(styles.heading)}>
                {subscriptionProduct && subscriptionProduct.metadata.homePage.carouselHeading ? (
                    <>{subscriptionProduct.metadata.homePage.carouselHeading}</>
                ) : (
                    <>
                        Say hello to&nbsp;
                        <br className={css(styles.breakpoint)} />
                        <span className={css(styles.highlight)}>Series</span>
                    </>
                )}
            </h3>
        );
    };

    public renderSeriesCarouselSubHeading = () => {
        const { subscriptionProduct, sectionData } = this.props;

        return (
            <p className={css(styles.subHeading)}>
                {subscriptionProduct && subscriptionProduct.metadata.homePage.carouselSubHeading ? (
                    <>{subscriptionProduct.metadata.homePage.carouselSubHeading}</>
                ) : (
                    sectionData.subTitle
                )}
            </p>
        );
    };

    public render() {
        const selectedPlaylists = this.getSelectedPlaylists();
        const currentSeries = selectedPlaylists && selectedPlaylists[this.state.currentSlide];

        const currentSeriesInfo = currentSeries && {
            title: currentSeries.name,
            articles:
                currentSeries.articles &&
                currentSeries.articles.map((a, index) => ({
                    episodeTitle: index === 0 ? 'Introduction' : `Episode ${index}`,
                    articleTitle: a.name,
                    publisher: {
                        name: a.publisher.name,
                        image: a.publisher.smallImage,
                    },
                })),
        };

        return (
            <div className={css(styles.main)}>
                <Container>
                    <Row>
                        <Col
                            xs={{ offset: 0, size: 12 }}
                            sm={{ offset: 1, size: 10 }}
                            md={{ offset: 2, size: 8 }}
                            className={css(styles.center)}>
                            {this.renderSeriesCarouselHeading()}
                            {this.renderSeriesCarouselSubHeading()}
                        </Col>
                    </Row>
                    <Row>
                        <Col className={css(styles.center)}>
                            <Select
                                options={this.getCategoryOptions()}
                                className={css(styles.useCaseDropdown)}
                                styles={customSelectStyes}
                                onChange={this.handleCategorySelected}
                                value={this.state.selectedCategoryOption}
                                isSearchable={false}
                            />

                            <div className={css(styles.carouselWrapper)}>
                                <div className={css(styles.roundBorder)} />
                                <IconCircledLeftArrow
                                    className={css(styles.arrowButtons, styles.leftButton)}
                                    onClick={this.goToLeftSlide}
                                />
                                <NoaCarousel
                                    slides={this.getSeriesSlides()}
                                    animationConfig={config.gentle}
                                    goToSlide={this.state.currentSlide}
                                />
                                <IconCircledRightArrow
                                    className={css(styles.arrowButtons, styles.rightButton)}
                                    onClick={this.goToRightSlide}
                                />
                            </div>

                            {currentSeriesInfo && currentSeriesInfo.articles && (
                                <div className={css(styles.seriesArticlesWrapper)}>
                                    {currentSeriesInfo.articles.map((a, index) => {
                                        const articleNum = index + 1;
                                        // @ts-ignore
                                        const lastItem = articleNum >= currentSeriesInfo.articles.length;

                                        return (
                                            <div
                                                className={css(
                                                    styles.articleWrapper,
                                                    articleNum > 4 && styles.hideMedium,
                                                    articleNum > 5 && styles.hideLarge,
                                                    articleNum > 6 && styles.hideExtraLarge,
                                                )}>
                                                <div className={css(styles.progressBar)}>
                                                    <div className={css(styles.progressCircle)} />
                                                    <div
                                                        className={css(
                                                            styles.progressLine,
                                                            lastItem && newtheme.coreStyles.hidden,
                                                            articleNum > 3 && styles.hideSmall,
                                                            articleNum > 3 && styles.hideMedium,
                                                            articleNum > 4 && styles.hideLarge,
                                                            articleNum > 5 && styles.hideExtraLarge,
                                                        )}
                                                    />
                                                </div>
                                                <img
                                                    alt={`${a.publisher.name} logo`}
                                                    key={`${a.articleTitle}_image`}
                                                    className={css(styles.articlePublisherImage)}
                                                    src={a.publisher.image}
                                                />
                                                <p
                                                    key={`${a.articleTitle}_episode`}
                                                    className={css(styles.articleEpisodeTitle)}>
                                                    {a.episodeTitle}
                                                </p>
                                                <p key={a.articleTitle} className={css(styles.articleTitle)}>
                                                    {a.articleTitle}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}

const customSelectStyes = {
    container: (provided) => ({
        ...provided,
        color: 'white',
        border: 'none',
        cursor: 'pointer',
    }),
    option: (provided) => ({
        ...provided,
        ':hover': {
            opacity: 0.7,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 14,
        },
        borderTop: '1px solid #04446c',
        backgroundColor: '#033758',
        padding: '15px 20px',
        color: newtheme.colors.primaryBlue,
        fontWeight: 600,
        fontSize: 18,
        cursor: 'pointer',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#033758',
        textAlign: 'center',
    }),
    control: (provided) => ({
        ...provided,
        border: 'none',
        backgroundColor: '#033758',
        color: 'white',
        borderRadius: 10,
        cursor: 'pointer',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: newtheme.colors.primaryBlue,
        fontWeight: 600,
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        stroke: newtheme.colors.primaryBlue,
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: newtheme.colors.primaryBlue,
        hover: {
            color: newtheme.colors.primaryBlue,
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        [newtheme.cssBreakpoints.large]: {
            fontSize: 22,
            padding: '10px 15px',
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 20,
            padding: '5px 15px',
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 18,
        },
        padding: '15px 15px',
    }),
    singleValue: (provided) => ({
        ...provided,
        [newtheme.cssBreakpoints.large]: {
            fontSize: 20,
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 16,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 16,
        },
        fontSize: 22,
        fontWeight: 600,
        color: newtheme.colors.primaryBlue,
    }),
};

const opacityKeyframes = {
    from: {
        opacity: 0,
    },

    to: {
        opacity: 1,
    },
};

const styles = StyleSheet.create({
    main: {
        [newtheme.cssBreakpoints.tiny]: {
            paddingBottom: 80,
        },
        position: 'relative',
        width: '100%',
        backgroundColor: 'white',
        overflow: 'hidden',
        zIndex: 1,
    },
    roundBorder: {
        [newtheme.cssBreakpoints.medium]: {
            width: 3000,
        },
        [newtheme.cssBreakpoints.small]: {
            width: 2500,
            bottom: '37%',
        },
        [newtheme.cssBreakpoints.tiny]: {
            width: 2000,
            bottom: '35%',
        },
        [newtheme.cssBreakpoints.smallerThanTiny]: {
            width: 2000,
            bottom: '38%',
        },
        position: 'absolute',
        bottom: '40%',
        left: '50%',
        transform: 'translateX(-50%)',
        height: 2700,
        width: 4000,
        borderBottomRightRadius: '50%',
        borderBottomLeftRadius: '50%',
        backgroundColor: '#031629',
        zIndex: -1,
    },
    center: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    heading: {
        [newtheme.cssBreakpoints.large]: {
            fontSize: 60,
        },
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 45,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 32,
        },
        [newtheme.cssBreakpoints.tiny]: {
            fontSize: 24,
        },
        textAlign: 'center',
        fontSize: 64,
        fontWeight: 500,
        marginTop: 40,
        marginBottom: 18,
    },
    subHeading: {
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 16,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 14,
            marginBottom: 25,
        },
        [newtheme.cssBreakpoints.tiny]: {
            marginLeft: 50,
            marginRight: 50,
        },
        [newtheme.cssBreakpoints.smallerThanTiny]: {
            fontSize: 12,
        },
        [newtheme.cssBreakpoints.minuscule]: {
            marginLeft: 30,
            marginRight: 30,
        },
        fontSize: 21,
        color: '#9CD6EE',
        lineHeight: '171%',
        marginBottom: 40,
        textAlign: 'center',
        alignSelf: 'stretch',
    },
    publishedEmbedSubHeading: {
        [newtheme.cssBreakpoints.small]: {
            marginBottom: 0,
        },
        marginBottom: 0,
        whiteSpace: 'pre-line',
    },
    useCaseDropdown: {
        [newtheme.cssBreakpoints.medium]: {
            width: 250,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 18,
        },
        width: 300,
        zIndex: 99,
    },
    breakpoint: {
        [newtheme.cssBreakpoints.tiny]: {
            // display: "block",
        },
        display: 'none',
    },
    highlight: {
        [newtheme.cssBreakpoints.tiny]: {
            fontSize: 36,
        },
        color: newtheme.colors.primaryBlue,
        fontWeight: 600,
    },
    carouselWrapper: {
        [newtheme.cssBreakpoints.medium]: {
            height: 320,
        },
        [newtheme.cssBreakpoints.small]: {
            height: 260,
        },
        [newtheme.cssBreakpoints.tiny]: {
            height: 210,
        },
        position: 'relative',
        height: 400,
        width: '100%',
    },
    arrowButtons: {
        [newtheme.cssBreakpoints.small]: {
            height: 70,
            width: 70,
        },
        [newtheme.cssBreakpoints.tiny]: {
            // top: "auto",
            // bottom: 0,
            ':hover': {
                filter: 'none',
            },
            height: 60,
            width: 60,
        },
        ':hover': {
            filter: 'brightness(70%)',
        },
        position: 'absolute',
        transform: 'translate(-50%, 0)',
        bottom: '33%',
        zIndex: 999,
        cursor: 'pointer',
        height: 100,
        width: 100,
    },
    leftButton: {
        [newtheme.cssBreakpoints.large]: {
            left: 'calc(50% - 370px)',
        },
        [newtheme.cssBreakpoints.medium]: {
            left: 'calc(50% - 300px)',
        },
        [newtheme.cssBreakpoints.small]: {
            left: 'calc(50% - 220px)',
        },
        [newtheme.cssBreakpoints.tiny]: {
            left: '3%',
        },
        left: 'calc(50% - 410px)',
    },
    rightButton: {
        [newtheme.cssBreakpoints.large]: {
            left: 'calc(50% + 370px)',
        },
        [newtheme.cssBreakpoints.medium]: {
            left: 'calc(50% + 300px)',
        },
        [newtheme.cssBreakpoints.small]: {
            left: 'calc(50% + 220px)',
        },
        [newtheme.cssBreakpoints.tiny]: {
            left: '97%',
        },
        left: 'calc(50% + 410px)',
    },
    seriesCard: {
        [newtheme.cssBreakpoints.medium]: {
            height: 240,
            width: 350,
        },
        [newtheme.cssBreakpoints.small]: {
            height: 188,
            width: 275,
        },
        [newtheme.cssBreakpoints.tiny]: {
            height: 160,
            padding: 20,
            paddingLeft: 15,
            paddingRight: 5,
            borderRadius: 15,
            boxShadow: 'none',
        },
        height: 280,
        width: 410,
        borderRadius: 27,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        boxShadow: '0px 14.0536px 30.2692px rgba(0, 0, 0, 0.2)',
        padding: 30,
        paddingRight: 0,
    },
    titleAndButton: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    title: {
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 21,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 18,
        },
        [newtheme.cssBreakpoints.tiny]: {
            fontSize: 16,
            margin: 0,
        },
        fontWeight: 600,
        fontSize: 26,
        flex: 1,
    },
    button: {
        [newtheme.cssBreakpoints.small]: {
            width: 90,
            height: 90,
        },
        [newtheme.cssBreakpoints.tiny]: {
            width: 75,
            height: 75,
        },
        position: 'relative',
        width: 126,
        height: 126,
        overflow: 'visible',
    },
    borderArrow: {
        position: 'absolute',
        top: -35,
        zIndex: -1,
    },
    seriesArticlesWrapper: {
        [newtheme.cssBreakpoints.large]: {
            padding: '35px 0px 35px 50px',
        },
        [newtheme.cssBreakpoints.medium]: {
            padding: '30px 0px 30px 40px',
            borderRadius: 20,
        },
        [newtheme.cssBreakpoints.small]: {
            border: '1px solid rgba(3, 22, 41, 0.17)',
            padding: '20px 0px 20px 60px',
            borderRadius: 10,
        },
        [newtheme.cssBreakpoints.tiny]: {
            paddingLeft: 40,
        },
        [newtheme.cssBreakpoints.smallerThanTiny]: {
            paddingLeft: 30,
        },
        [newtheme.cssBreakpoints.minuscule]: {
            paddingLeft: 15,
        },
        position: 'relative',
        zIndex: 2,
        width: '100%',
        color: 'black',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        border: '2px solid rgba(3, 22, 41, 0.17)',
        backgroundColor: 'white',
        padding: '40px 0px 40px 40px',
        borderRadius: 30,
    },
    articleWrapper: {
        [newtheme.cssBreakpoints.tiny]: {
            padding: '0px 0px',
        },
        display: 'flex',
        flexDirection: 'column',
        padding: '0px 0px',
        alignItems: 'flex-start',
        flex: 1,
        width: '100%',
    },
    articlePublisherImage: {
        [newtheme.cssBreakpoints.small]: {
            width: 35,
            height: 35,
            marginBottom: 10,
            borderRadius: 5,
        },
        [newtheme.cssBreakpoints.tiny]: {
            width: 25,
            height: 25,
            marginBottom: 10,
            borderRadius: 4,
        },
        width: 60,
        height: 60,
        zIndex: 99999,
        borderRadius: 10,
        marginBottom: 20,
        animationName: [opacityKeyframes],
        animationDuration: '1s',
        backgroundColor: '#d3d3d3',
        filter: 'drop-shadow(0px 4.32418px 14.0536px rgba(0, 0, 0, 0.15))',
    },
    articleEpisodeTitle: {
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 12,
        },
        [newtheme.cssBreakpoints.small]: {
            fontSize: 12,
            backgroundColor: 'white',
            color: 'black',
            padding: 0,
            minWidth: 'auto',
            marginBottom: 0,
        },
        [newtheme.cssBreakpoints.tiny]: {
            fontSize: 10,
            fontWeight: 500,
            borderRadius: 5,
        },
        display: 'inline-block',
        fontSize: 14,
        fontWeight: 500,
        padding: '5px 15px',
        backgroundColor: 'black',
        color: 'white',
        marginBottom: 20,
        borderRadius: 10,
        minWidth: 120,
        textAlign: 'center',
        animationName: [opacityKeyframes],
        animationDuration: '1s',
    },
    articleTitle: {
        [newtheme.cssBreakpoints.medium]: {
            fontSize: 12,
        },
        [newtheme.cssBreakpoints.small]: {
            display: 'none',
        },
        fontSize: 14,
        margin: 0,
        width: '100%',
        paddingRight: 40,
        animationName: [opacityKeyframes],
        animationDuration: '1s',
    },
    progressBar: {
        [newtheme.cssBreakpoints.small]: {
            paddingRight: 10,
        },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        paddingRight: 20,
        marginBottom: 15,
    },
    progressCircle: {
        [newtheme.cssBreakpoints.small]: {
            width: 10,
            height: 10,
            borderRadius: 5,
            marginRight: 8,
        },
        [newtheme.cssBreakpoints.tiny]: {
            width: 6,
            height: 6,
            borderRadius: 3,
        },
        height: 12,
        width: 12,
        marginRight: 20,
        borderRadius: 6,
        backgroundColor: newtheme.colors.primaryBlue,
    },
    progressLine: {
        border: `1px dashed ${newtheme.colors.primaryBlue}`,
        opacity: 0.4,
        flex: 1,
    },
    hideExtraLarge: {
        [newtheme.cssBreakpoints.greaterThanLarge]: {
            display: 'none',
        },
    },
    hideLarge: {
        [newtheme.cssBreakpoints.large]: {
            display: 'none',
        },
    },
    hideMedium: {
        [newtheme.cssBreakpoints.medium]: {
            display: 'none',
        },
    },
    hideSmall: {
        [newtheme.cssBreakpoints.small]: {
            display: 'none',
        },
    },
});

function mapStateToProps(state: IState): IPropsFromState {
    return {
        loading: state.section.loading,
        sections: state.section.sections,
        selectedSection: state.section.section as ISectionWithPlaylists,
        subscriptionProduct: getSelectedSubscriptionProduct(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch): IPropsFromDispatch {
    return bindActionCreators(
        {
            getSectionsV4,
            getSingleSectionV4,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(SeriesExplainer);
